/**
 * TrlImageLibraryContext
 *
 * The Trailblazers module of the Content Library: the global screenshot library
 * that is shared across Trailblazers games rather than owned by a single game
 * instance. Loaded over HTTP (refreshable) with upload/update/delete/share
 * helpers.
 *
 * Mounted app-level (via ContentLibraryProviders) for every authenticated admin
 * — independent of the selected game — so the Content Library page works with
 * no game selected, and so the round settings image picker can read the library
 * regardless of game selection.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { TrlImageAsset } from '@swtorbit/shared-socket-events';
import {
  listTrlImages,
  uploadTrlImage,
  updateTrlImage,
  deleteTrlImage,
  setTrlImageShared,
  type TrlImageUploadFields,
  type TrlImagePatch,
} from './trlImageApi';
import { gameLogger } from '../../../utils/logger';

interface TrlImageLibraryContextType {
  images: TrlImageAsset[];
  imagesLoading: boolean;
  imagesError: string | null;
  refreshImages: () => Promise<void>;
  uploadImage: (fields: TrlImageUploadFields, file: File) => Promise<TrlImageAsset>;
  updateImage: (id: string, patch: TrlImagePatch) => Promise<TrlImageAsset>;
  deleteImage: (id: string) => Promise<void>;
  setImageShared: (id: string, shared: boolean) => Promise<TrlImageAsset>;
}

const TrlImageLibraryContext = createContext<TrlImageLibraryContextType | undefined>(undefined);

interface TrlImageLibraryProviderProps {
  children: ReactNode;
}

export function TrlImageLibraryProvider({ children }: TrlImageLibraryProviderProps) {
  const [images, setImages] = useState<TrlImageAsset[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesError, setImagesError] = useState<string | null>(null);

  const refreshImages = useCallback(async () => {
    setImagesLoading(true);
    setImagesError(null);
    try {
      const list = await listTrlImages();
      setImages(list);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load images';
      setImagesError(message);
      gameLogger.warn('TRL image library load failed', { message });
    } finally {
      setImagesLoading(false);
    }
  }, []);

  // Load the library on mount.
  useEffect(() => {
    void refreshImages();
  }, [refreshImages]);

  const uploadImage = useCallback(
    async (fields: TrlImageUploadFields, file: File) => {
      const image = await uploadTrlImage(fields, file);
      await refreshImages();
      return image;
    },
    [refreshImages]
  );

  const updateImage = useCallback(
    async (id: string, patch: TrlImagePatch) => {
      const image = await updateTrlImage(id, patch);
      await refreshImages();
      return image;
    },
    [refreshImages]
  );

  const deleteImage = useCallback(
    async (id: string) => {
      await deleteTrlImage(id);
      await refreshImages();
    },
    [refreshImages]
  );

  const setImageShared = useCallback(
    async (id: string, shared: boolean) => {
      const image = await setTrlImageShared(id, shared);
      await refreshImages();
      return image;
    },
    [refreshImages]
  );

  const value = useMemo(
    () => ({
      images,
      imagesLoading,
      imagesError,
      refreshImages,
      uploadImage,
      updateImage,
      deleteImage,
      setImageShared,
    }),
    [images, imagesLoading, imagesError, refreshImages, uploadImage, updateImage, deleteImage, setImageShared]
  );

  return <TrlImageLibraryContext.Provider value={value}>{children}</TrlImageLibraryContext.Provider>;
}

export function useTrlImageLibrary() {
  const context = useContext(TrlImageLibraryContext);
  if (context === undefined) {
    throw new Error('useTrlImageLibrary must be used within a TrlImageLibraryProvider');
  }
  return context;
}
